import { ref, computed, onMounted, onBeforeUnmount, unref } from 'vue'
import { fetchCaptures } from '@/services/monitoringApi'

export const CAPTURE_KINDS = {
  visual: { label: 'PlanetScope · Visual' },
  ndvi: { label: 'PlanetScope · NDVI' },
  skysat: { label: 'SkySat · Hi-Res' }
}

export function captureKindLabel(kind) {
  return CAPTURE_KINDS[kind]?.label ?? kind
}

export function captureKey(capture) {
  return `${capture.kind}:${capture.feature_code}:${capture.item_date}`
}

export function bucketLabel(iso, now = new Date()) {
  const d = new Date(iso)
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (sameDay(d, now)) return 'Today'
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, yesterday)) return 'Yesterday'
  const sameMonth = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  if (sameMonth) return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  if (d.getFullYear() === prevMonth.getFullYear() && d.getMonth() === prevMonth.getMonth()) {
    return 'Last month'
  }
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

export function groupByBucket(captures) {
  const sorted = [...captures].sort((a, b) => Date.parse(b.item_date) - Date.parse(a.item_date))
  const groups = []
  for (const item of sorted) {
    const label = bucketLabel(item.item_date)
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.items.push(item)
    else groups.push({ label, items: [item] })
  }
  return groups
}

export function useCaptures(orgId, projectId, { pollMs = 60_000 } = {}) {
  const captures = ref([])
  const isLoading = ref(false)
  let timer = null

  async function load() {
    isLoading.value = true
    try {
      const data = await fetchCaptures(unref(orgId), unref(projectId))
      captures.value = data.captures || []
    } catch (e) {
      console.error('Failed to fetch captures', e)
    } finally {
      isLoading.value = false
    }
  }

  const buckets = computed(() => groupByBucket(captures.value))

  onMounted(() => {
    load()
    timer = setInterval(load, pollMs)
  })

  onBeforeUnmount(() => {
    clearInterval(timer)
  })

  return { captures, buckets, isLoading, load }
}
